/**
 * Strips the data URL prefix (e.g. "data:image/png;base64,") from a base64 string.
 * Returns the raw base64 data, or the original value if no prefix is present.
 */
export const stripBase64Prefix = (
  value: string | null | undefined,
): string | null | undefined =>
  value?.includes(',') ? value.split(',')[1] : value

/**
 * Fetches an image from a URL and returns it as a base64 data URL.
 */
export const urlToBase64 = (url: string): Promise<string> =>
  fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch image: ${res.status}`)
      }
      return res.blob()
    })
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(blob)
        }),
    )

/**
 * Converts a data URL (e.g. "data:image/png;base64,...") to a Blob.
 */
export const base64ToBlob = (
  base64: string,
  fallbackMimeType = 'image/png',
): Blob => {
  const [header, data] = base64.split(',')
  const mimeMatch = header.match(/:(.*?);/)
  const mimeType = mimeMatch ? mimeMatch[1] : fallbackMimeType

  const byteCharacters = atob(data)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type: mimeType })
}
